import { AI_LEVELS, type AiLevel, type Color, Game, type GameStatus, getVariant, win } from '@chessbut/engine';
import { useSyncExternalStore } from 'react';
import { api } from '../lib/api.ts';
import { load, save } from '../lib/storage.ts';
import type { AiRequest, AiResponse } from './ai.worker.ts';

export type LocalMode = 'bot' | 'local' | 'challenge';

export interface LocalGameConfig {
  variant: string;
  mode: LocalMode;
  /** The human's colour (bot and challenge games). */
  human: Color;
  level: number;
  /** Daily challenge: the day and the bot's seed. */
  day?: string;
  seed?: number;
}

interface SavedGame extends LocalGameConfig {
  moves: string[];
  resigned: GameStatus | null;
  startedAt: number;
  reported: boolean;
  /** Whether the server heard the game started (first move). */
  announced?: boolean;
}

const MIN_THINK_MS = 450;

let worker: Worker | null = null;
function aiWorker(): Worker {
  worker ??= new Worker(new URL('./ai.worker.ts', import.meta.url), { type: 'module' });
  return worker;
}

/** Ids for AI requests, unique across every game on the page (they share one worker). */
let nextRequestId = 0;

export function saveKeyFor(config: Pick<LocalGameConfig, 'variant' | 'mode' | 'day'>): string {
  return config.mode === 'challenge' ? `challenge:${config.day}` : `game:${config.variant}:${config.mode}`;
}

/** A game played on this device: against the computer, pass & play, or the daily challenge. */
export class LocalGame {
  readonly game: Game;
  version = 0;
  thinking = false;
  readonly startedAt: number;
  /** Whether the result was handed to the upload queue. */
  reported: boolean;
  private announced: boolean;
  private announcing = false;
  private disposed = false;
  private resigned: GameStatus | null;
  private requestId = 0;
  private pendingListener: ((event: MessageEvent<AiResponse>) => void) | null = null;
  private readonly listeners = new Set<() => void>();
  private snapshot = { version: 0, thinking: false };

  constructor(
    readonly config: LocalGameConfig,
    saved?: Pick<SavedGame, 'moves' | 'resigned' | 'startedAt' | 'reported' | 'announced'>,
  ) {
    const variant = getVariant(config.variant);
    if (!variant) throw new Error(`Unknown variant ${config.variant}`);
    this.game = Game.fromMoves(variant, saved?.moves ?? []);
    this.resigned = saved?.resigned ?? null;
    this.startedAt = saved?.startedAt ?? Date.now();
    this.reported = saved?.reported ?? false;
    this.announced = saved?.announced ?? this.game.ply > 0;
    this.save();
  }

  static restore(key: string): LocalGame | null {
    const saved = load<SavedGame | null>(key, null);
    if (!saved) return null;
    try {
      return new LocalGame(saved, saved);
    } catch {
      return null;
    }
  }

  static clear(key: string) {
    save(key, null);
  }

  get key(): string {
    return saveKeyFor(this.config);
  }

  get level(): AiLevel {
    return AI_LEVELS.find((l) => l.id === this.config.level) ?? AI_LEVELS[1];
  }

  /** Lets the computer move if it's its turn (call once the game is on screen). */
  resume() {
    this.disposed = false;
    this.maybeThink();
  }

  subscribe = (l: () => void) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };

  getSnapshot = () => this.snapshot;

  private changed() {
    this.version++;
    this.snapshot = { version: this.version, thinking: this.thinking };
    this.save();
    for (const l of this.listeners) l();
  }

  private save() {
    const saved: SavedGame = {
      ...this.config,
      moves: this.game.history.map((r) => r.uci),
      resigned: this.resigned,
      startedAt: this.startedAt,
      reported: this.reported,
      announced: this.announced,
    };
    save(this.key, saved);
  }

  /**
   * Tells the server a game started (analytics; the challenge's server-side timer). Tried on
   * every move until the server has acknowledged it.
   */
  private announce() {
    if (this.announced || this.announcing) return;
    this.announcing = true;
    const c = this.config;
    const req =
      c.mode === 'challenge'
        ? api('/api/challenge/start', { body: { day: c.day } })
        : api('/api/play-start', { body: { variant: c.variant, mode: c.mode, level: c.mode === 'bot' ? c.level : undefined } });
    req
      .then(() => {
        this.announced = true;
        // A replaced game mustn't overwrite the save of the one that took its place.
        if (!this.disposed) this.save();
      })
      .catch(() => {})
      .finally(() => {
        this.announcing = false;
      });
  }

  markReported() {
    this.reported = true;
    this.save();
  }

  status(): GameStatus {
    return this.resigned ?? this.game.status();
  }

  get vsBot(): boolean {
    return this.config.mode !== 'local';
  }

  canMove(color: Color): boolean {
    if (this.status().over || this.thinking) return false;
    return !this.vsBot || color === this.config.human;
  }

  move(uci: string): boolean {
    if (!this.canMove(this.game.turn)) return false;
    if (!this.game.play(uci)) return false;
    this.announce();
    this.changed();
    this.maybeThink();
    return true;
  }

  private maybeThink() {
    if (this.thinking || !this.vsBot || this.status().over || this.game.turn === this.config.human) return;
    const id = ++nextRequestId;
    this.requestId = id;
    this.thinking = true;
    this.snapshot = { version: this.version, thinking: true };
    for (const l of this.listeners) l();
    const started = performance.now();
    const w = aiWorker();
    this.dropListener();
    const onMessage = (event: MessageEvent<AiResponse>) => {
      if (event.data.id !== id) return;
      w.removeEventListener('message', onMessage);
      if (this.pendingListener === onMessage) this.pendingListener = null;
      const wait = Math.max(0, MIN_THINK_MS - (performance.now() - started));
      setTimeout(() => {
        if (id !== this.requestId) return;
        this.thinking = false;
        if (event.data.uci) this.game.play(event.data.uci);
        this.changed();
        this.maybeThink();
      }, wait);
    };
    w.addEventListener('message', onMessage);
    this.pendingListener = onMessage;
    const req: AiRequest = {
      id,
      variant: this.config.variant,
      moves: this.game.history.map((r) => r.uci),
      level: this.config.level,
      seed: this.config.mode === 'challenge' ? this.config.seed : undefined,
    };
    w.postMessage(req);
  }

  /** Forgets an AI request that's no longer wanted (undo, resign, leaving the screen). */
  private dropListener() {
    if (this.pendingListener) worker?.removeEventListener('message', this.pendingListener);
    this.pendingListener = null;
  }

  canUndo(): boolean {
    if (this.resigned || this.config.mode === 'challenge' || this.status().over) return false;
    if (this.config.mode === 'local') return this.game.ply > 0;
    return this.game.history.some((r) => r.color === this.config.human);
  }

  /** Takes back the last move (vs computer: back to the human's last turn). */
  undo() {
    if (!this.canUndo()) return;
    this.requestId = 0;
    this.dropListener();
    this.thinking = false;
    if (this.config.mode === 'local') this.game.undo();
    else {
      let removedHumanMove = false;
      while (this.game.ply > 0 && (!removedHumanMove || this.game.turn !== this.config.human)) {
        if (this.game.undo()?.color === this.config.human) removedHumanMove = true;
      }
    }
    this.changed();
  }

  resign() {
    if (this.status().over) return;
    const loser = this.vsBot ? this.config.human : this.game.turn;
    this.requestId = 0;
    this.dropListener();
    this.thinking = false;
    this.resigned = win(loser === 0 ? 1 : 0, 'resign');
    this.changed();
  }

  dispose() {
    this.disposed = true;
    this.requestId = 0;
    // A search still running for a game nobody is looking at would hold up the next game's
    // (they share the worker), so stop it.
    if (this.pendingListener) {
      worker?.terminate();
      worker = null;
    }
    this.dropListener();
    this.thinking = false;
    this.listeners.clear();
  }
}

export function useLocalGame(game: LocalGame) {
  return useSyncExternalStore(game.subscribe, game.getSnapshot);
}
